import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { ReactComponent as SettingsIcon } from '@material-design-icons/svg/outlined/settings.svg'
import { ReactComponent as ArrowBackIcon } from '@material-design-icons/svg/round/arrow_back_ios.svg'
import { ReactComponent as CloseIcon } from '@material-design-icons/svg/round/close.svg'
import { ReactComponent as ShareIcon } from '@material-design-icons/svg/round/share.svg'
import Container from 'components/common/Container'
import Button from 'components/common/Button'
import IconLink from 'components/common/IconLink'
import IconButton from 'components/common/IconButton'
import MainLayoutContext from 'contexts/MainLayoutContext'
import { useAppSelector } from 'hooks/store'
import useLocationInfo from 'hooks/useLocationInfo'
import useShare from 'hooks/useShare'
import useLogOut from 'hooks/query/auth/useLogOut'
import logo from 'images/logo.png'
import c from './MainLayoutHeader.module.scss'

const getNavLinkClasses = ({ isActive }: { isActive: boolean }): string => {
  let result = c.navigationButton
  if (isActive) result += ` ${c.navigationButtonActive}`
  return result
}

function MainLayoutHeader() {
  const {
    stage,
    setStage,
    processedAssets,
    currentAssetIndex,
    openLogInModal,
    openSignUpModal,
  } = useContext(MainLayoutContext)

  const user = useAppSelector((state) => state.auth.user)

  const currentAssetUrl = useMemo(() => {
    const currentAsset = processedAssets[currentAssetIndex]
    return currentAsset?.url
  }, [processedAssets, currentAssetIndex])

  const { t } = useTranslation()

  const { pathname, isNested, heading, backTo } = useLocationInfo()

  const { handleShare } = useShare(currentAssetUrl)

  const { mutate: logOut, error } = useLogOut()

  const handleLogOut = useCallback(() => {
    logOut()
  }, [logOut])

  const isEnhancementPage = useMemo(() => pathname === '', [pathname])
  const isGenerationPage = useMemo(() => pathname === '/neuro-art', [pathname])

  const previewStageHeading = useMemo(() => {
    if (isEnhancementPage) return t('stages.enhancement.preview.heading')
    return ''
  }, [t, isEnhancementPage])

  const processingStageHeading = useMemo(() => {
    if (isEnhancementPage) return t('stages.enhancement.processing.heading')
    if (isGenerationPage) return t('stages.generation.processing.heading')
    return ''
  }, [t, isEnhancementPage, isGenerationPage])

  const finishStageHeading = useMemo(() => {
    if (isEnhancementPage) return t('stages.enhancement.finish.heading')
    if (isGenerationPage) return t('stages.generation.finish.heading')
    return ''
  }, [t, isEnhancementPage, isGenerationPage])

  const handleGoBack = useCallback(() => {
    setStage('idle')
  }, [])

  useEffect(() => {
    if (error?.message) toast.error(error.message)
  }, [error?.message])

  const headerContent = useMemo(() => {
    if (isEnhancementPage || isGenerationPage) {
      if (stage === 'preview') {
        return (
          <>
            <IconButton onClick={handleGoBack}>
              <ArrowBackIcon style={{ fill: 'currentColor' }} />
            </IconButton>
            <p className={c.heading}>{previewStageHeading}</p>
            <div className={c.buttonFiller} />
          </>
        )
      }

      if (stage === 'processing') {
        return (
          <>
            <IconButton onClick={handleGoBack}>
              <CloseIcon style={{ fill: 'currentColor' }} />
            </IconButton>
            <p className={c.heading}>{processingStageHeading}</p>
            <div className={c.buttonFiller} />
          </>
        )
      }

      if (stage === 'finish') {
        return (
          <>
            <IconButton onClick={handleGoBack}>
              <CloseIcon style={{ fill: 'currentColor' }} />
            </IconButton>
            <p className={c.heading}>{finishStageHeading}</p>
            <IconButton onClick={handleShare}>
              <ShareIcon style={{ fill: 'currentColor' }} />
            </IconButton>
          </>
        )
      }

      return (
        <div className={c.grid}>
          <div className={c.logo}>
            <img className={c.logoImage} alt="Logo" src={logo} />
            <p className={c.logoText}>Retomagic</p>
          </div>
          <div className={c.navigation}>
            <NavLink className={getNavLinkClasses} to="/" end>
              {t('navigation.enhancement')}
            </NavLink>
            <NavLink className={getNavLinkClasses} to="/neuro-art">
              {t('navigation.generation')}
            </NavLink>
          </div>
          <div className={c.controls}>
            <button className={c.buttonPro} type="button">
              Pro
            </button>
            <div className={c.auth}>
              {user ? (
                <Button
                  className={c.authButton}
                  variant="text"
                  onClick={handleLogOut}
                >
                  {t('general.auth.logOut')}
                </Button>
              ) : (
                <>
                  <Button
                    className={c.authButton}
                    variant="text"
                    onClick={openLogInModal}
                  >
                    {t('general.auth.logIn')}
                  </Button>
                  <Button
                    className={c.authButton}
                    variant="text"
                    onClick={openSignUpModal}
                  >
                    {t('general.auth.signUp')}
                  </Button>
                </>
              )}
            </div>
            <IconLink to="/settings">
              <SettingsIcon style={{ fill: 'currentColor' }} />
            </IconLink>
          </div>
        </div>
      )
    }

    return (
      <>
        <IconLink to={backTo}>
          {isNested ? (
            <ArrowBackIcon style={{ fill: 'currentColor' }} />
          ) : (
            <CloseIcon style={{ fill: 'currentColor' }} />
          )}
        </IconLink>
        <p className={c.heading}>{heading}</p>
        <div className={c.buttonFiller} />
      </>
    )
  }, [
    t,
    user,
    stage,
    pathname,
    previewStageHeading,
    processingStageHeading,
    finishStageHeading,
    isNested,
    heading,
    backTo,
    handleGoBack,
    handleShare,
    handleLogOut,
    openLogInModal,
    openSignUpModal,
  ])

  return (
    <header className={c.header}>
      <Container>
        <div className={c.container}>{headerContent}</div>
      </Container>
    </header>
  )
}

export default MainLayoutHeader
